/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Reports browser crashes to POST /api/client-error, which feeds the same ring
// buffer the admin dashboard's "kritieke fouten" widget reads.
//
// Why this exists: until now a crash in the browser produced a console.error and
// nothing else. A React hydration mismatch (#418) that throws away the whole
// client tree on a prerendered page — the exact failure that hit About and the
// homepage in production — left no trace anywhere an admin looks. The error
// boundary even told the visitor "we've been notified", which was not true.
//
// Consent: nothing here identifies a visitor. No cookie is read or written, no
// id, no user agent, no query string (the server drops it too). It is the same
// first-party operational beacon shape as the outbound/share pings, and like
// them it is not gated on cookie consent.

import type { ClientErrorKind } from '../types/api/observability';
import { isChunkLoadError, reloadOnceForStaleBundle } from './staleBundle';

// The kinds are ONE definition shared with the server's allow-list
// (server/data/clientErrors.ts); re-exported for the error boundary.
export type { ClientErrorKind };

export interface ClientErrorInput {
  kind: ClientErrorKind;
  message: string;
  stack?: string;
  componentStack?: string;
  /**
   * True when a boundary with a null fallback absorbed the crash: an optional
   * overlay went missing and the visitor got a complete page. The server
   * records such a report as a warning, not a crash.
   */
  contained?: boolean;
}

/**
 * A rejection reason is not always an Error. A wallet extension rejects with
 * a bare `{ code, message }`, a helper may reject with a string, an Error
 * from another realm (an iframe) fails `instanceof` — and `String({})` is the
 * useless "[object Object]" the log carried until now.
 */
export function describeRejectionReason(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    return { message: reason.message || reason.name || 'Error', stack: reason.stack };
  }
  if (reason && typeof reason === 'object') {
    const r = reason as Record<string, unknown>;
    const stack = typeof r.stack === 'string' ? r.stack : undefined;
    if (typeof r.message === 'string' && r.message) {
      const code = r.code === undefined || r.code === null ? '' : ` (code ${String(r.code)})`;
      return { message: `${r.message}${code}`, stack };
    }
    try {
      const json = JSON.stringify(reason);
      if (json && json !== '{}') return { message: json, stack };
    } catch {
      /* cyclic — fall through to the tag */
    }
    return { message: `Unhandled rejection ${Object.prototype.toString.call(reason)}`, stack };
  }
  if (reason === undefined || reason === null || reason === '') {
    return { message: 'Unhandled rejection' };
  }
  return { message: String(reason) };
}

/**
 * A broken page can produce the same crash on every render attempt, and a
 * hydration failure walks the whole tree. Both caps are per pageload.
 */
const MAX_REPORTS_PER_PAGELOAD = 5;
const MAX_STACK_CHARS = 1500;

const seen = new Set<string>();
let sent = 0;

/** Test seam — the caps are module state, so a test needs to clear them. */
export function _resetClientErrorReporterForTests(): void {
  seen.clear();
  sent = 0;
}

function post(body: string): void {
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    // sendBeacon survives the navigation a visitor is likely to make right
    // after seeing an error screen (reload, back, home).
    navigator.sendBeacon('/api/client-error', new Blob([body], { type: 'application/json' }));
    return;
  }
  void fetch('/api/client-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    // Reporting a failed report would be the second error on a page that is
    // already broken. This is the one place a silent catch is the right call.
  });
}

/**
 * Fire-and-forget. Never throws and never returns a promise: every caller is
 * already on a failure path, where an exception would replace a recoverable
 * problem with an unrecoverable one.
 */
export function reportClientError(input: ClientErrorInput): void {
  try {
    if (typeof window === 'undefined') return; // SSR / prerender
    const message = String(input.message ?? '').slice(0, 300);
    if (!message) return;

    const path = window.location?.pathname ?? '';
    const signature = `${input.kind}|${path}|${message}`;
    if (seen.has(signature)) return;
    if (sent >= MAX_REPORTS_PER_PAGELOAD) return;
    seen.add(signature);
    sent += 1;

    post(
      JSON.stringify({
        kind: input.kind,
        message,
        path,
        stack: input.stack ? input.stack.slice(0, MAX_STACK_CHARS) : undefined,
        componentStack: input.componentStack
          ? input.componentStack.slice(0, MAX_STACK_CHARS)
          : undefined,
        contained: input.contained ? true : undefined,
      })
    );
  } catch {
    /* a reporter that throws is worse than no reporter */
  }
}

/**
 * True for the cross-origin report shape: a script served from another origin
 * without CORS (and every browser extension) surfaces as a bare "Script error."
 * with no file, no line and no stack. Those entries are unactionable and would
 * bury the real ones, so they are the one thing this handler drops.
 *
 * Exported for tests — the filter is the whole reason hooking `error` is safe.
 */
export function isOpaqueCrossOriginError(event: {
  filename?: string;
  lineno?: number;
  error?: unknown;
}): boolean {
  if (event.error instanceof Error && event.error.stack) return false;
  return !event.filename || !event.lineno;
}

/**
 * Catches the two failure classes that never reach a React error boundary:
 *
 *  - rejected promises (an effect, a background write). Without this a save
 *    that silently died is exactly the kind of thing reported as "it didn't
 *    work" with nothing to go on.
 *  - synchronous throws outside React entirely — an inline script, a third-party
 *    widget, a global handler. These were previously invisible: hooking
 *    `window.onerror` was skipped wholesale because cross-origin scripts report
 *    as an unactionable bare "Script error.". Filtering on that exact shape
 *    (see isOpaqueCrossOriginError) keeps the noise out and the signal in.
 */
export function installGlobalClientErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    const { message, stack } = describeRejectionReason(reason);
    // A lazy import called outside React (an effect, a prefetch) that failed:
    // the same incident the boundary reports as `chunk`, and a warning while
    // the stale-bundle reload is under way (src/lib/staleBundle.ts).
    if (isChunkLoadError(reason)) {
      reportClientError({
        kind: 'chunk',
        message,
        stack,
        contained: reloadOnceForStaleBundle() || undefined,
      });
      return;
    }
    reportClientError({ kind: 'unhandled', message, stack });
  });

  window.addEventListener('error', (event) => {
    // A failed <img>/<script>/<link> load also fires 'error' on window, with the
    // element as target and no Error attached. A missing image is not a crash.
    if (typeof ErrorEvent === 'undefined' || !(event instanceof ErrorEvent)) return;
    if (isOpaqueCrossOriginError(event)) return;
    const err = event.error instanceof Error ? event.error : undefined;
    reportClientError({
      kind: 'unhandled',
      message: err?.message ?? event.message ?? 'Uncaught error',
      stack: err?.stack,
    });
  });
}
